import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@pondigitalsolutions/pon-component-library";
import { twMerge } from "tailwind-merge";

import { useDropdownToggle } from "@hooks/useDropdownToggle";
import { useUpdatePreference } from "@hooks/useUpdatePreference";
import { type AutoTranslateLanguage } from "../../types/StoryblokTypes";

import {
  AUTO_TRANSLATE_LANGUAGE_OPTIONS,
  AutoTranslateLanguageMenu,
} from "./AutoTranslateLanguageMenu";

interface AutoTranslateLanguageDropdownProps {
  enabled: boolean;
  language: AutoTranslateLanguage | null;
}

export const AutoTranslateLanguageDropdown = ({
  enabled,
  language,
}: AutoTranslateLanguageDropdownProps) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const { isOpen, close, toggle } = useDropdownToggle(ref);
  const { mutate: updatePreferences } = useUpdatePreference();

  const triggerLabel = language
    ? (AUTO_TRANSLATE_LANGUAGE_OPTIONS.find((o) => o.code === language)
        ?.label ?? t("Settings.autoTranslateSelectPlaceholder"))
    : t("Settings.autoTranslateSelectPlaceholder");

  const handleToggle = () => {
    if (!enabled) return;
    toggle();
  };

  return (
    <div
      ref={ref}
      className="relative"
      data-testid="auto-translate-language-dropdown"
    >
      <Button
        id="auto-translate-language"
        size="lg"
        variant="secondary"
        endIcon="ChevronDown"
        disabled={!enabled}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        ariaLabel={t("Settings.autoTranslateLanguageAriaLabel")}
        onClick={handleToggle}
        className={twMerge(
          "h-fit min-h-8 w-fit min-w-56 cursor-pointer justify-between border-tertiary-200 text-left dark:border-white/20 dark:bg-white/20",
          !enabled && "cursor-not-allowed opacity-50"
        )}
      >
        {triggerLabel}
      </Button>
      <AutoTranslateLanguageMenu
        isOpen={isOpen}
        id="auto-translate-language-menu"
        language={language}
        onSelect={(code) => {
          updatePreferences({ autoTranslateLanguage: code });
          close();
        }}
        className="top-12 right-auto left-0"
      />
    </div>
  );
};
